import React from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import { Box, Grid, makeStyles } from '@material-ui/core';
import LoadingSpinner from 'components/LoadingSpinner';
import ContextMenu from 'components/shared/ContextMenu';
import { Message } from 'components/shared';
import { DocumentButton, DocumentMenuItem } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    padding: '.5rem',
    width: '100%',
    '& a': {
      textDecoration: 'none',
      color: 'inherit'
    }
  },
  categories: {
    width: '100%',
    display: 'flex'
  },
  categoryButton: {
    flexGrow: 1,
    color: theme.palette.primary.main,
    textTransform: 'none',
    height: '60px',
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '0',
    '&:first-child': {
      borderRadius: '4px 0 0 4px'
    },
    '&:last-child': {
      borderRadius: '0 4px 4px 0'
    },
    '&.active': {
      fontWeight: theme.typography.fontWeightBold,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.white
    }
  },
  documentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    columnGap: '.25rem',
    rowGap: '.25rem',
    marginTop: '.25rem'
  },
  contextMenuCaption: {
    fontWeight: theme.typography.fontWeightBold,
    paddingBottom: '8px',
    color: theme.palette.grey.dark,
    borderBottom: `2px solid ${theme.palette.accent.main}`,
    textTransform: 'uppercase',
    textAlign: 'center'
  }
}));

const DocumentsWidget = ({ state, forwardRef }) => {
  const { data, isLoading, isError, error } = state;
  const classes = useStyles();

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !data) {
    return <LoadingSpinner />;
  }

  const renderDocumentButtons = () => {
    return data.buttons.map(button => {
      if (button.options[0].documentId === 0) {
        return (
          <DocumentButton
            key={button.caption}
            caption={button.caption}
            document={button.options.find(o => o.rank === 1)}
          />
        );
      }

      const menuComponent = (
        <Box className={classes.contextMenu}>
          <Box className={classes.contextMenuCaption}>{button.caption}</Box>
          {button.options.map(option => (
            <DocumentMenuItem key={`${option.documentType}-${option.rank}`} document={option} />
          ))}
        </Box>
      );

      const menuId = `menu-${nanoid(6)}`;
      return (
        <ContextMenu key={menuId} menuId={menuId} menuComponent={menuComponent}>
          <DocumentButton
            caption={button.caption}
            document={button.options.find(o => o.rank === 1)}
          />
        </ContextMenu>
      );
    });
  };

  return (
    <Grid container direction="row" className={classes.root}>
      {!data || isLoading ? (
        <LoadingSpinner />
      ) : (
        <Grid ref={forwardRef} item xs={12} className={classes.documentGrid}>
          {renderDocumentButtons()}
        </Grid>
      )}
    </Grid>
  );
};

DocumentsWidget.propTypes = {
  state: PropTypes.shape({
    data: PropTypes.shape({
      buttons: PropTypes.arrayOf(
        PropTypes.shape({
          caption: PropTypes.string,
          options: PropTypes.arrayOf(
            PropTypes.shape({
              rank: PropTypes.number,
              documentId: PropTypes.number
            })
          )
        })
      )
    }),
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string
  }).isRequired,
  forwardRef: PropTypes.shape({})
};

DocumentsWidget.defaultProps = {
  forwardRef: null
};
export default DocumentsWidget;
